import { v4 as uuidv4 } from "uuid"

import { DEFAULTS } from "@/graphql/schemas/shared/utils/defaults"

import { getAssignedCustomers } from "../permissions/permissions"

/**
 * Helper function to build query for sorting and limiting
 *
 * @param {sort: Sort, offset: number, limit: number, sortWithinLimits: bool, includeDrafts: bool }
 * @returns { pipesAndLimits: string, draftsFilter: string }
 */
export const groqQueryOptions = ({ sort, offset, limit, sortWithinLimits, includeDrafts }) => {
  const paramSort = getSortParam(sort)
  const paramOffset = offset ?? 0
  const paramLimit = limit ?? -1

  // apply offset and limit
  let limits = ""
  if (paramOffset >= 0 && paramLimit > 0) limits = `[${paramOffset}..${paramOffset + paramLimit - 1}]`
  if (paramOffset >= 0 && paramLimit < 1) limits = `[${paramOffset}..${paramOffset + DEFAULTS.QUERY.LIMIT}]`

  const pipesAndLimits = sortWithinLimits ? `${limits}${paramSort}` : `${paramSort}${limits}`
  const draftsFilter = includeDrafts ? `&& !(_id in path("drafts.**"))` : ""

  return {
    pipesAndLimits,
    draftsFilter,
  }
}

export const getAssignedCustomersFilter = async ({ user }) => {
  const admAssignedCustomers = await getAssignedCustomers({
    admNumber: user?.loggedInAdm.admNumber,
  })
  if (!admAssignedCustomers) return ""
  const customerNumbers = formatToGroqFilterInValue(admAssignedCustomers)
  return `&& customerNumber in ${customerNumbers}`
}

function formatToGroqFilterInValue(admAssignedCustomers) {
  return JSON.stringify(admAssignedCustomers.map((customer) => customer.customerNumber))
}

function getSortParam(sort) {
  if (!sort) return ""
  return sort.caseSensitive
    ? `| order(coalesce(${sort.orderBy},-1) ${sort.direction})`
    : `| order(coalesce(lower(${sort.orderBy}),-1) ${sort.direction})`
}

export async function createAttachmentReferences(attachments, client) {
  if (!attachments || attachments?.length === 0) return null

  const results = []
  for (const attachment of attachments) {
    const type = fileOrImage(attachment?.mimetype)

    if (attachment?.id) {
      const { id, ...rest } = attachment
      results.push({ _key: id, _type: `${type}Reference`, ...rest })
    }

    if (!attachment?.base64String) continue

    const blob = b64toBlob(attachment?.base64String, attachments.mimetype)
    try {
      const result = await client.assets.upload(type, blob, {
        filename: attachment.name,
      })

      results.push({
        _type: `${type}Reference`,
        asset: {
          _ref: result._id,
          _type: "reference",
        },
        size: result.size,
        mimetype: result.mimeType,
        url: result.url,
        name: result.originalFilename,
        _key: uuidv4(),
      })
    } catch (e) {
      console.error({ e })
      // TODO handle this
    }
  }
  return results
}

function b64toBlob(b64Data, contentType = "", sliceSize = 512) {
  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentType })
}

function fileOrImage(mimeType) {
  const imageTypes = ["image/jpeg", "image/png"]

  if (imageTypes.includes(mimeType)) {
    return "image"
  } else {
    return "file"
  }
}

export async function deleteAttachmentReferences(attachments, client) {
  if (attachments.length === 0) return null

  const results = []
  for (const attachment of attachments) {
    if (!attachment?.id || !attachment?.asset?._ref) continue

    try {
      const result = await client.delete(attachment.asset._ref)

      results.push(result)
    } catch (e) {
      console.error(e)
      // TODO handle this
    }
  }
  return results
}

export function getUpdatedAttachments(visitReportAttachments, editedAttachments) {
  const updatedAttachments = editedAttachments?.map((editedAttachment) => {
    // if attachment id is already in sanity, attachment was not modified and we keep the attachment data stored in sanity
    const unmodifiedAttachment = visitReportAttachments?.find(
      (attachmentFromSanity) => attachmentFromSanity.id === editedAttachment.id,
    )
    return unmodifiedAttachment ? unmodifiedAttachment : editedAttachment
  })

  return updatedAttachments?.map((attachment) => {
    return {
      name: attachment.name,
      mimetype: attachment.mimetype,
      size: attachment.size,
      base64String: attachment.base64String,
      _key: uuidv4(),
    }
  })
}

export const hasAttachmentsChanged = (attachmentLengthInSanity, updatedAttachments) => {
  return (
    attachmentLengthInSanity !== updatedAttachments?.length || updatedAttachments?.some((attachment) => !attachment.id)
  )
}
